/**
 * 统一处理服务端传来的 websocket 消息
 * 对消息进行一些通用处理：格式解析、分类分发等
 * 
 * 处理不涉及业务逻辑，对消息的业务逻辑处理应当在 LiveCore 中或品类继承 LiveCore 后单独实现
 */
 import {
    ConnectionCallBackEvent,
    IConnectionCallBackResult,
    IConnectionCallBackServerMsg,
    IDisconnectReason,
    IServerMessage,
    IServerMessageBodyNoticeUserLeave,
    IServerMessageBodyNoticeUserNewInfo,
} from "@tencent/docs-connection";
import WebsocketClient from ".";
import { ILiveSocketMessageHandler } from './websocket-client.interface';

enum MessageType {
    // 必要消息
    USER_NEWINFO = "USER_NEWINFO",
    USER_NEWINFOS = "USER_NEWINFOS",
    USER_LEAVE = "USER_LEAVE",
    USER_LEAVES = "USER_LEAVES",
    ROOM_NEWINFO = "ROOM_NEWINFO",
    LIVE_MESSAGE = "LIVE_MESSAGE",
}

export default class MsgFromServerHandler {

    /**
     * 连接层消息处理集合
     */
    private connectionCallBackEventMap = new Map([
        [`${ConnectionCallBackEvent.onRoomOpen}`, () => this.handleOnRoomOpen()],
        [`${ConnectionCallBackEvent.onRoomClose}`, (result: IConnectionCallBackResult) => this.handleOnRoomClose(result.data?.closeReason)],
        [`${ConnectionCallBackEvent.onRoomShutDown}`,(result: IConnectionCallBackResult) => this.handleOnRoomShutDown(result.data?.closeReason),],
        [`${ConnectionCallBackEvent.onReceiveMsgFromServer}`,(result: IConnectionCallBackResult) => this.handleOnReceiveMsgFromServer(result.data?.serverMsg),],
    ]);

    /**
     * 远程演示消息处理集合
     */
    private liveMessageCallBackEventMap = {
        [MessageType.USER_NEWINFO]: (msg: IServerMessage) => this.handleUserNewInfo(msg),
        [MessageType.USER_NEWINFOS]: (msg: IServerMessage) => this.handleUserNewInfos(msg),
        [MessageType.USER_LEAVE]: (msg: IServerMessage) => this.handleUserLeave(msg),
        [MessageType.USER_LEAVES]: (msg: IServerMessage) => this.handleUserLeaves(msg),
        [MessageType.ROOM_NEWINFO]: (msg: IServerMessage) => this.handleRoomNewInfo(msg),
        [MessageType.LIVE_MESSAGE]: (msg: IServerMessage) => this.handleLiveMessage(msg),
    };

    constructor(private liveCoreMessageHandler: ILiveSocketMessageHandler, private client: WebsocketClient) {}

    /**
     * 处理连接层消息
     * @param result 来自后台服务的连接层消息，消息一般由连接层定义
     */
    public handleCallbackEvent(result: IConnectionCallBackResult) {
        this.connectionCallBackEventMap.get(result.event)?.(result);
    }

    /**
     * 处理远程演示消息
     * @param serverMsg 来自后台服务的原始消息，是远程演示定义的
     */
    public handleAllMsgFromServer(serverMsg: IConnectionCallBackServerMsg) {
        serverMsg.data.forEach((item) => {
            this.liveMessageCallBackEventMap[serverMsg.type as MessageType](item);
        });
    }

    /**
     * 连接层房间连接成功事件
     */
     private handleOnRoomOpen() {
        this.liveCoreMessageHandler.handleSocketOpen?.();
    }

    /**
     * 连接层房间关闭事件
     * @param reason 关闭原因
     */
    private handleOnRoomClose(reason?: IDisconnectReason) {
        // DO NOTHING. docs-connection 中有重连逻辑
    }

    /**
     * 连接层房间终止事件
     * @param reason 关闭原因
     */
    private handleOnRoomShutDown(reason?: IDisconnectReason) {
        // shutDown 后主动关闭, 不再重连
        this.liveCoreMessageHandler.handleSocketShutDown?.(reason);
        this.client.close();
    }

    /**
     * 连接层其他透传事件
     * @param serverMsg 事件结构体
     */
    private handleOnReceiveMsgFromServer(serverMsg?: IConnectionCallBackServerMsg) {
        if (serverMsg) {
            this.handleAllMsgFromServer(serverMsg);
        }
    }

    /**
     * 远程演示事件处理：新用户加入房间
     * @param data 服务端原始消息
     */
    private handleUserNewInfo(data?: IServerMessage) {
        const userData = data?.body?.notice as IServerMessageBodyNoticeUserNewInfo;
        this.liveCoreMessageHandler.handleUserEnter?.(userData);
    };

    /**
     * 远程演示事件处理：新用户批量加入房间
     * @param data 服务端原始消息
     */
    private handleUserNewInfos(data?: IServerMessage) {
        const userDataList = data?.body?.notices as IServerMessageBodyNoticeUserNewInfo[];
        userDataList?.forEach((userData) => {
            this.liveCoreMessageHandler.handleUserEnter?.(userData);
        })
    };

    /**
     * 远程演示事件处理：用户离开房间
     * @param data 服务端原始消息
     */
    private handleUserLeave(data?: IServerMessage) {
        const userData = data?.body?.notice as IServerMessageBodyNoticeUserLeave;
        this.liveCoreMessageHandler.handleUserLeave?.(userData);
    };

    /**
     * 远程演示事件处理：用户批量离开房间
     * @param data 服务端原始消息
     */
     private handleUserLeaves(data?: IServerMessage) {
        const userDataList = data?.body?.notices as IServerMessageBodyNoticeUserLeave[];
        userDataList?.forEach((userData) => {
            this.liveCoreMessageHandler.handleUserLeave?.(userData);
        })
    };

    /**
     * 远程演示事件处理：新加入的房间信息
     * @param data 服务端原始消息
     */
    private handleRoomNewInfo(data?: IServerMessage) {
        const infoData = data?.body?.notice;
        this.liveCoreMessageHandler.handleRoomNewInfo?.(infoData);
    };

    /**
     * 远程演示事件处理：各种演示信息
     * @param data 服务端原始消息
     */
    private handleLiveMessage(data?: IServerMessage) {
        const infoData = data?.body?.notice;
        this.liveCoreMessageHandler.handleLiveMessage?.(infoData);
    };
}